import React, { useEffect, useState } from 'react';
import SelectDropdown from 'components/base/select-dropdown';
import {
  Dropdown,
  DropdownItem,
  DropdownList,
  Input,
  ToastMessage
} from '@cokitchen/cokitchen-components';
import { weightMeasurement } from 'utils/constants';
import './styles.scss';
import { AddProcurementType } from 'models/procurement.model';
import useDashboardContext from 'contexts/dashboard.context';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import IngredientSelct from 'components/form-comps/ingredients-select';
import { formatCurrency } from 'utils/functions';

const AddProcurementForm = ({
  data,
  index,
  setValue,
  removeItem,
  totalItems,
  canWrite = true
}: {
  data: AddProcurementType;
  index: number;
  setValue: (index: number, field: keyof AddProcurementType, value: string) => void;
  removeItem: (index: number) => void;
  totalItems: number;
  canWrite?: boolean;
}) => {
  const { formStoreItems, formVendorsList } = useDashboardContext();
  const getIngredient = (id: string) => formStoreItems?.find((ingredient) => ingredient.id === id);
  const [isFetchingVendorIngredients, setIsFetchingVendorIngredients] = useState(false);
  const [vendorIngredients, setVendorIngredients] = useState<
    { id: string; name: string; price: string; unit_type: string; vendor_id: string }[]
  >([]);
  const getVendorNameAndPrice = (id: string) => formVendorsList?.find((vendor) => vendor.id === id);

  const fetchVendorIngredients = async (id: string) => {
    try {
      setIsFetchingVendorIngredients(true);
      const response = await apiService.get(`vendors/ingredients?ingredient_id=${id}`);
      if (response) {
        setVendorIngredients(
          response?.data?.results.map((vendor_ingredient: any) => {
            return {
              id: vendor_ingredient.id,
              name: vendor_ingredient.ingredient.name,
              price: '₦ ' + Number(vendor_ingredient.gram_price).toLocaleString(),
              unit_type: vendor_ingredient.ingredient.unit_type,
              vendor_id: vendor_ingredient.vendor_id
            };
          })
        );
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching vendor ingredients'}
          type='error'
        />
      );
    } finally {
      setIsFetchingVendorIngredients(false);
    }
  };

  useEffect(() => {
    if (data.ingredient_id) {
      setVendorIngredients([]);
      fetchVendorIngredients(data.ingredient_id);
    }
  }, [data.ingredient_id]);

  const unitType = getIngredient(data?.ingredient_id)?.unit_type;

  return (
    <div className={`add__procurement pb-4 ${index > 0 ? 'pt-6' : ''} border-b border-[#EDEDEE]`}>
      <div>
        <div className='grid grid-cols-2 gap-6 mb-6 flex-wrap'>
          <div>
            <p className='font-medium mb-2'>Item name</p>
            <IngredientSelct
              value={data?.ingredient_name}
              setValue={(item) => {
                if (!canWrite) return;
                setValue(index, 'ingredient_id', item.id);
                setValue(index, 'ingredient_name', item.name);
                setValue(index, 'vendor_ingredient_unit_type', item.unit_type);
              }}
            />
          </div>
          <div className='relative'>
            <Input
              design='bordered'
              label='Quantity'
              placeholder='00'
              value={data.quantity}
              disabled={!canWrite}
              onChange={(e) => setValue(index, 'quantity', e.target.value)}
            />
            <div className='absolute w-[50px] h-[20px] z-10 rounded-[2px] shadow bottom-[20%] right-[5%]'>
              <Dropdown
                disabled
                content={
                  <div className='h-5 p-1 text-[11px] flex justify-between items-center gap-[2px] cursor-pointer measurement_dropdown'>
                    <span className={`text-[#6F7376] truncate ${unitType ? 'capitalize' : ''}`}>
                      {unitType ?? 'Unit(s)'}
                    </span>

                    {/* <ArrowDownIcon className='w-5 h-5 text-[#96999C]' /> */}
                  </div>
                }>
                <DropdownList>
                  {weightMeasurement.map((item) => (
                    <DropdownItem
                      key={item.value}
                      onClick={() => {
                        return;
                      }}>
                      {item.name}
                    </DropdownItem>
                  ))}
                </DropdownList>
              </Dropdown>
            </div>
          </div>
          <div className='col-span-2'>
            <SelectDropdown
              options={
                !data.ingredient_id
                  ? [{ id: '', name: 'Select an ingredient' }]
                  : vendorIngredients.length > 0
                  ? vendorIngredients.map((vendor_ingredient) => {
                      return {
                        id: vendor_ingredient.id,
                        name: getVendorNameAndPrice(vendor_ingredient.vendor_id)?.name || '',
                        price: vendor_ingredient.price,
                        unit_type: vendor_ingredient.unit_type,
                        vendor_id: vendor_ingredient.vendor_id
                      };
                    })
                  : [{ id: '', name: 'Ingredient not available at any vendor' }]
              }
              loading_options={isFetchingVendorIngredients}
              value={getVendorNameAndPrice(data.vendor_id)?.name}
              price_value={data.vendor_ingredient_price}
              placeholder='Select a vendor'
              setValue={(value: {
                id: string;
                name: string;
                price: string;
                unit_type: string;
                vendor_id: string;
                vendor_name: string;
              }) => {
                if (!canWrite) return;
                setValue(index, 'vendor_ingredient_id', value.id);
                setValue(index, 'vendor_id', value.vendor_id);
                setValue(index, 'vendor_ingredient_price', value.price);
                setValue(index, 'vendor_ingredient_unit_type', value.unit_type);
              }}
              label='Vendor'
            />
            {data.quantity && data.vendor_ingredient_price && (
              <h3 className='text-sm mt-2 text-gray-5'>
                Total:
                {formatCurrency(
                  Number(data.quantity) *
                    Number(data.vendor_ingredient_price.replace(/[₦,\s]/g, ''))
                )}
              </h3>
            )}{' '}
          </div>
        </div>
        {canWrite && totalItems > 1 && (
          <button
            onClick={() => removeItem(index)}
            className='text-[#E60A2B] mt-2 font-medium text-sm '>
            - Remove Item
          </button>
        )}
      </div>
    </div>
  );
};

export default AddProcurementForm;
